import React, { useState, useMemo } from 'react';
import { Row, Col, DatePicker, Button, Input, Modal, message } from 'antd';
import dayjs from 'dayjs';
import { PieChartOutlined, EyeOutlined, EyeInvisibleOutlined, LockOutlined } from '@ant-design/icons';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import { BsFillPersonFill } from 'react-icons/bs';
import { FiArrowUpRight } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { FaCalendarCheck } from 'react-icons/fa';
import { DATE_FORMAT, MENUS, SUPER_ADMIN_EMAILS } from 'constants/app-constants';
import { useI18n } from 'hooks/useI18n';
import { LineChartGradient } from 'components/charts/line-chart';
import RecentPayments from './recent-payments';
import DashboardCard from './dashboard-card';
import UpcomingBirthdays from './upcoming-birthdays';
import TopReferrers from './top-referrers';
import KnownBySources from './known-by-sources';
import SubscriptionExpiryWarning from './subscription-expiry-warning';
import { DASHBOARD_ACCESS_EVENT, clearDashboardAccess, grantDashboardAccess, hasDashboardProtection, isDashboardAccessGranted } from './dashboard-access';

const formatCompactCurrency = (value) => {
	const numericValue = Number(value || 0);
	return new Intl.NumberFormat('en-IN', {
		style: 'currency',
		currency: 'INR',
		notation: 'compact',
		maximumFractionDigits: 1,
	}).format(numericValue);
};

const formatExactCurrency = (value) => {
	const numericValue = Number(value || 0);
	return new Intl.NumberFormat('en-IN', {
		style: 'currency',
		currency: 'INR',
		maximumFractionDigits: 0,
	}).format(numericValue);
};

const MASKED_VALUE = '••••••';

const useElementSize = (active) => {
	const elementRef = React.useRef(null);
	const [size, setSize] = React.useState({ width: 0, height: 0 });

	React.useEffect(() => {
		if (!active || !elementRef.current) {
			return undefined;
		}

		const updateSize = () => {
			if (!elementRef.current) {
				return;
			}

			setSize({
				width: elementRef.current.offsetWidth,
				height: elementRef.current.offsetHeight,
			});
		};

		updateSize();

		const resizeObserver = new ResizeObserver(updateSize);
		resizeObserver.observe(elementRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, [active]);

	return { elementRef, size };
};

const UnlockCelebration = ({ className = '', active = false }) => {
	const { elementRef, size } = useElementSize(active);

	return (
		<div ref={elementRef} className={`dashboard-unlock-celebration ${className}`.trim()} aria-hidden="true">
			{active && size.width > 0 && size.height > 0 ? (
				<Confetti
					width={size.width}
					height={size.height}
					recycle={false}
					numberOfPieces={240}
					gravity={0.1}
					initialVelocityY={11}
					initialVelocityX={5}
					tweenDuration={12000}
					colors={['#4d7cfe', '#19a974', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899']}
					confettiSource={{ x: 0, y: Math.max(size.height - 42, 0), w: size.width, h: 42 }}
					run={active}
				/>
			) : null}
		</div>
	);
};

const ProtectedValue = ({ protectedView, visibleValue, maskedValue = MASKED_VALUE, className = '', maskedClassName = '' }) => (
	<span className={`dashboard-protected-value ${className}`.trim()}>
		<span className={`dashboard-protected-value__content dashboard-protected-value__content--actual ${protectedView ? 'is-hidden' : 'is-visible'}`.trim()}>{visibleValue}</span>
		<span className={`dashboard-protected-value__content dashboard-protected-value__content--masked ${maskedClassName} ${protectedView ? 'is-visible' : 'is-hidden'}`.trim()}>{maskedValue}</span>
	</span>
);

const formatMetricValue = (value) => {
	if (typeof value === 'number') {
		return value.toLocaleString('en-IN');
	}

	if (value === undefined || value === null || value === '') {
		return '0';
	}

	return value;
};

// Reusable Skeleton Card component
const SkeletonCard = () => (
	<Col xs={24} sm={12} lg={8} xl={4} className="dashboard-card-wrapper dashboard-card-skeleton-wrapper">
		<div className="dashboard_card dashboard-card-skeleton">
			<div className="card-topline">
				<div className="metric-copy">
					<span className="skeleton-title" />
					<div className="skeleton-desc">
						<span />
						<span />
					</div>
				</div>
				<span className="skeleton-icon" />
			</div>
			<div className="value-row">
				<span className="skeleton-value" />
				<span className="skeleton-chip" />
			</div>
		</div>
	</Col>
);

const DashboardPresentational = ({ dashboardData, loginRedux, getDashboardData, payments, loading, upcomingBirthdays, topReferrers }) => {
	const navigate = useNavigate();
	const [month, setMonth] = useState(dayjs());
	const [dashboardUnlocked, setDashboardUnlocked] = useState(() => isDashboardAccessGranted());
	const [accessModalOpen, setAccessModalOpen] = useState(false);
	const [dashboardPasswordInput, setDashboardPasswordInput] = useState('');
	const [showUnlockCelebration, setShowUnlockCelebration] = useState(false);
	const productSaleData = useSelector((state) => state.dashboardRedux.productSales);
	const membershipSales = useSelector((state) => state.dashboardRedux.membershipSales);
	const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(loginRedux.email);
	const { t } = useI18n();
	const knownByData = useMemo(() => dashboardData?.knownBy || {}, [dashboardData?.knownBy]);
	const displayName = [loginRedux?.firstName, loginRedux?.lastName].filter(Boolean).join(' ');
	const dashboardProtectionEnabled = hasDashboardProtection();
	const dashboardProtected = dashboardProtectionEnabled && !dashboardUnlocked;
	const correctPassword = localStorage.getItem('dashboardPassword');

	React.useEffect(() => {
		const syncDashboardAccess = () => {
			setDashboardUnlocked(isDashboardAccessGranted());
		};

		window.addEventListener(DASHBOARD_ACCESS_EVENT, syncDashboardAccess);
		window.addEventListener('storage', syncDashboardAccess);

		return () => {
			window.removeEventListener(DASHBOARD_ACCESS_EVENT, syncDashboardAccess);
			window.removeEventListener('storage', syncDashboardAccess);
		};
	}, []);

	React.useEffect(() => {
		if (!showUnlockCelebration) {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			setShowUnlockCelebration(false);
		}, 4200);

		return () => window.clearTimeout(timeoutId);
	}, [showUnlockCelebration]);

	const onMonthChange = (selectedMonth) => {
		if (selectedMonth) {
			setMonth(selectedMonth);
			getDashboardData(new Date(dayjs(selectedMonth)));
		}
	};

	const openDashboardAccessModal = () => {
		if (!dashboardProtectionEnabled) {
			return;
		}

		setAccessModalOpen(true);
	};

	const closeDashboardAccessModal = () => {
		setAccessModalOpen(false);
		setDashboardPasswordInput('');
	};

	const handleDashboardAccessSubmit = () => {
		if (!correctPassword || dashboardPasswordInput === correctPassword) {
			grantDashboardAccess();
			setDashboardUnlocked(true);
			setShowUnlockCelebration(false);
			window.requestAnimationFrame(() => setShowUnlockCelebration(true));
			closeDashboardAccessModal();
			return;
		}

		message.error(t('dashboard_card.incorrect_password'));
	};

	const handleDashboardAccessToggle = () => {
		if (!dashboardProtectionEnabled) {
			return;
		}

		if (dashboardProtected) {
			openDashboardAccessModal();
			return;
		}

		clearDashboardAccess();
		setDashboardUnlocked(false);
	};

	const cards = useMemo(() => {
		const superAdminCards = [
			{
				icon: <PieChartOutlined />,
				value: dashboardData?.thisMonthData?.totalJoined,
				name: t('dashboard.joined_this_month'),
				description: t('dashboard.customers_joined'),
			},
			{
				icon: <FiArrowUpRight />,
				value: dashboardData?.thisMonthData?.totalIncome,
				name: t('dashboard.earnings'),
				description: t('dashboard.total_earnings'),
				withPassword: true,
				sensitive: true,
			},
			{
				icon: <BsFillPersonFill />,
				value: dashboardData?.totalCustomers,
				name: t('dashboard.total_customers'),
				description: t('dashboard.total_customers_description'),
				onClick: () => navigate(MENUS[1].route),
			},
			{
				icon: <BsFillPersonFill />,
				value: dashboardData?.activeCustomers,
				name: t('dashboard.active_customers'),
				description: t('dashboard.active_customers_description'),
				onClick: () => navigate(MENUS[1].route),
			},
			{
				icon: <BsFillPersonFill />,
				value: dashboardData?.inactiveCustomers,
				name: t('dashboard.inactive_customers'),
				description: t('dashboard.inactive_customers_description'),
				onClick: () => navigate(MENUS[1].route),
			},
			{
				icon: <FaCalendarCheck />,
				value: dashboardData?.outStanding?.fees,
				name: t('dashboard.fees_outstanding'),
				description: t('dashboard.total_fees_outstanding'),
				onClick: () => navigate(MENUS[8].route),
				withPassword: true,
				sensitive: true,
			},
			{
				icon: <FiArrowUpRight />,
				value: dashboardData?.renewedThisMonthCustomers,
				name: t('dashboard.renewed_this_month'),
				description: t('dashboard.customers_renewed'),
			},
		];

		const earningsCards = [
			{
				icon: <FiArrowUpRight />,
				value: dashboardData?.thisMonthData?.totalIncome,
				name: t('dashboard.earnings'),
				description: t('dashboard.total_earnings'),
				withPassword: true,
				sensitive: true,
			},
			{
				icon: <FiArrowUpRight />,
				value: dashboardData?.thisMonthData?.totalPTEarningsThisMonth,
				name: t('dashboard.pt_earnings'),
				description: t('dashboard.total_pt_earnings'),
				withPassword: true,
				sensitive: true,
			},
			{
				icon: <FiArrowUpRight />,
				value: dashboardData?.manualExpence?.totalIncome,
				name: t('total_income_manual'),
				description: t('dashboard.total_income_manual_description'),
				withPassword: true,
				sensitive: true,
			},
			{
				icon: <FiArrowUpRight />,
				value: dashboardData?.manualExpence?.totalBalance,
				name: t('total_balance_manual'),
				description: t('dashboard.total_balance_manual_description'),
				withPassword: true,
				sensitive: true,
			},
		];

		const outstandingCards = [
			{
				icon: <FaCalendarCheck />,
				value: dashboardData?.outStanding?.fees,
				name: t('dashboard.fees_outstanding'),
				description: t('dashboard.total_fees_outstanding'),
				onClick: () => navigate(MENUS[8].route),
				withPassword: true,
				sensitive: true,
			},
			{
				icon: <FaCalendarCheck />,
				value: dashboardData?.outStanding?.supplement,
				name: t('supplement_outstanding'),
				description: t('dashboard.supplement_outstanding_description'),
				onClick: () => navigate(MENUS[8].route),
				withPassword: true,
				sensitive: true,
			},
			{
				icon: <FaCalendarCheck />,
				value: dashboardData?.outStanding?.pt,
				name: t('pt_outstandings'),
				description: t('dashboard.pt_outstandings_description'),
				withPassword: true,
				sensitive: true,
			},
		];

		const customerAndCoachCards = [
			{
				icon: <BsFillPersonFill />,
				value: dashboardData?.totalCustomers,
				name: t('dashboard.total_customers'),
				description: t('dashboard.total_customers_description'),
				onClick: () => navigate(MENUS[1].route),
			},
			{
				icon: <BsFillPersonFill />,
				value: dashboardData?.activeCustomers,
				name: t('dashboard.active_customers'),
				description: t('dashboard.active_customers_description'),
				onClick: () => navigate(MENUS[1].route),
			},
			{
				icon: <BsFillPersonFill />,
				value: dashboardData?.inactiveCustomers,
				name: t('dashboard.inactive_customers'),
				description: t('dashboard.inactive_customers_description'),
				onClick: () => navigate(MENUS[1].route),
			},
			{
				icon: <PieChartOutlined />,
				value: dashboardData?.totalCoaches,
				name: t('dashboard.total_coaches'),
				description: t('dashboard.active_coaches'),
				onClick: () => navigate(MENUS[4].route),
			},
			{
				icon: <FaCalendarCheck />,
				value: dashboardData?.totalVisitors,
				name: t('dashboard.total_visitors'),
				description: t('dashboard.visitors_this_month'),
				onClick: () => navigate(MENUS[2].route),
			},
		];

		const otherCards = [
			{
				icon: <PieChartOutlined />,
				value: dashboardData?.thisMonthData?.totalJoined,
				name: t('dashboard.joined_this_month'),
				description: t('dashboard.customers_joined'),
			},
			{
				icon: <FiArrowUpRight />,
				value: dashboardData?.renewedThisMonthCustomers,
				name: t('dashboard.renewed_this_month'),
				description: t('dashboard.customers_renewed'),
			},
			{ icon: <FiArrowUpRight />, value: dashboardData?.totalPTCount, name: t('total_pt_count'), description: t('total_personal_trainings') },
		];

		const nonSuperAdminCards = [...earningsCards, ...outstandingCards, ...customerAndCoachCards, ...otherCards];

		return isSuperAdmin ? superAdminCards : nonSuperAdminCards;
	}, [dashboardData, navigate, isSuperAdmin, t]);

	const heroHighlights = useMemo(() => cards.slice(0, isSuperAdmin ? 4 : 3), [cards, isSuperAdmin]);
	const shouldShowProductSales = useMemo(() => {
		const series = Array.isArray(productSaleData?.data?.data) ? productSaleData.data.data : [];
		return series.some((value) => Number(value || 0) > 0);
	}, [productSaleData]);

	return (
		<Row className="dashboard_container">
			<Col span={24}>
				<Row gutter={[10, 10]}>
					<Col span={24}>
						<section className="dashboard-hero">
							<UnlockCelebration className="dashboard-unlock-celebration--hero" active={showUnlockCelebration} />
							<Row className="dashboard-header-row" align="middle" justify="space-between" gutter={[16, 16]}>
								<Col xs={24} lg={16}>
									<div className="dashboard-hero__content">
										<span className="dashboard-hero__eyebrow">{t('dashboard.title')}</span>
										<h4 className="dashboard-greeting">
											{t('dashboard.greeting')}, {displayName}! <span className="dashboard-greeting__wave">👋</span>
										</h4>
										<p className="dashboard-hero__subtitle">{t('dashboard.welcome')}</p>
									</div>
								</Col>
								<Col xs={24} lg={8} xl={6}>
									<div className="dashboard-hero__picker-card">
										{dashboardProtectionEnabled ? (
											<Button className="dashboard-visibility-toggle" type={dashboardProtected ? 'primary' : 'default'} icon={dashboardProtected ? <EyeOutlined /> : <EyeInvisibleOutlined />} onClick={handleDashboardAccessToggle}>
												{dashboardProtected ? t('dashboard.show_amounts') : t('dashboard.hide_amounts')}
											</Button>
										) : null}
										<DatePicker
											className="dashboard-month-picker"
											style={{ width: '100%' }}
											picker="month"
											value={month}
											autoComplete="off"
											format={DATE_FORMAT.MMM_YYYY}
											onChange={onMonthChange}
										/>
									</div>
								</Col>
							</Row>
							<div className="dashboard-hero__highlights">
								{heroHighlights.map((card, index) => (
									<div key={`${card.name}-${index}`} className="dashboard-hero__highlight">
										<span className="dashboard-hero__highlight-label">{card.name}</span>
										<strong className="dashboard-hero__highlight-value">
											<ProtectedValue protectedView={dashboardProtected && card.sensitive} visibleValue={formatMetricValue(card.value)} className="dashboard-protected-value--hero" maskedClassName="dashboard-protected-value__content--hero-masked" />
										</strong>
									</div>
								))}
							</div>
						</section>
					</Col>
					{/* Subscription Expiry Warning */}
					{/* 
						You can customize the warning threshold by passing warningThresholdDays prop.
						Example: <SubscriptionExpiryWarning nextBillDate={loginRedux?.nextBillDate} warningThresholdDays={15} />
						Default is 30 days if not specified.
					*/}
					<Col span={24}>
						<SubscriptionExpiryWarning nextBillDate={loginRedux?.nextBillDate} />
					</Col>
					<Col span={24}>
						<Row gutter={[12, 12]}>
							{loading
								? Array(cards.length)
										.fill(null)
										.map((_, index) => <SkeletonCard key={index} />)
								: cards.map((card, index) => <DashboardCard key={index} {...card} />)}
						</Row>
					</Col>
					{isSuperAdmin ? (
						<>
							<Col span={24} style={{ paddingTop: 20 }}>
								<Row gutter={[10, 10]}>
									<Col xs={24} md={24} lg={24}>
										<SalesCard
											title={t('dashboard.membership_sales')}
											data={membershipSales?.data}
											selectedMonth={month}
											protectedView={dashboardProtected}
											dashboardProtectionEnabled={dashboardProtectionEnabled}
											onAccessToggle={handleDashboardAccessToggle}
											celebrateUnlock={showUnlockCelebration}
										/>
									</Col>
								</Row>
							</Col>
							<Col span={24}>
								<Row gutter={[10, 10]}>
									<Col span={24}>
										<RecentPayments payments={payments} />
									</Col>
								</Row>
							</Col>
						</>
					) : (
						<>
							<Col span={24} style={{ paddingTop: 20 }}>
								<Row gutter={[10, 10]}>
									<Col xs={24} md={shouldShowProductSales ? 12 : 24}>
										<SalesCard
											title={t('dashboard.membership_sales')}
											data={membershipSales?.data}
											selectedMonth={month}
											protectedView={dashboardProtected}
											dashboardProtectionEnabled={dashboardProtectionEnabled}
											onAccessToggle={handleDashboardAccessToggle}
											celebrateUnlock={showUnlockCelebration}
										/>
									</Col>
											{shouldShowProductSales ? (
												<Col xs={24} md={12}>
													<SalesCard
														title={t('dashboard.product_sales')}
														data={productSaleData?.data}
														selectedMonth={month}
														protectedView={dashboardProtected}
														dashboardProtectionEnabled={dashboardProtectionEnabled}
														onAccessToggle={handleDashboardAccessToggle}
														celebrateUnlock={showUnlockCelebration}
														hideWhenEmpty
													/>
												</Col>
											) : null}
								</Row>
							</Col>
							<Col span={24}>
								<Row gutter={[10, 10]}>
									<Col span={24}>
										<RecentPayments payments={payments} />
									</Col>
									<Col xs={24} md={12} lg={8}>
										<UpcomingBirthdays birthdays={upcomingBirthdays} loading={loading} />
									</Col>
									<Col xs={24} md={12} lg={8}>
										<TopReferrers referrers={topReferrers} loading={loading} />
									</Col>
									<Col xs={24} md={12} lg={8}>
										<KnownBySources knownByData={knownByData} loading={loading} />
									</Col>
								</Row>
							</Col>
						</>
					)}
				</Row>
			</Col>
			<Modal
				className="dashboard-access-modal"
				title={
					<div className="dashboard-access-modal__title">
						<LockOutlined />
						{t('dashboard.enter_password_to_view_data')}
					</div>
				}
				open={accessModalOpen}
				onOk={handleDashboardAccessSubmit}
				onCancel={closeDashboardAccessModal}
				okText={t('dashboard.show_amounts')}
				cancelText={t('common.cancel')}
				centered>
				<div className="dashboard-access-modal__body">
					<p className="dashboard-access-modal__copy">{t('dashboard.unlock_financial_data')}</p>
					<Input.Password
						prefix={<LockOutlined />}
						placeholder={t('dashboard_card.enter_current_password')}
						size="large"
						value={dashboardPasswordInput}
						onChange={(e) => setDashboardPasswordInput(e.target.value)}
						onPressEnter={handleDashboardAccessSubmit}
						autoFocus
					/>
				</div>
			</Modal>
		</Row>
	);
};

const SalesCard = ({ title, data, selectedMonth, protectedView, dashboardProtectionEnabled, onAccessToggle, celebrateUnlock, hideWhenEmpty = false }) => {
	const { t } = useI18n();
	const series = Array.isArray(data?.data) ? data.data : [];
	const currentValue = series[series.length - 1] || 0;
	const previousValue = series[series.length - 2] || 0;
	const hasData = series.some((value) => Number(value || 0) > 0);
	const currentLabel = selectedMonth ? dayjs(selectedMonth).format(DATE_FORMAT.MMM_YYYY) : '';
	const trend = previousValue > 0 ? Math.round(((currentValue - previousValue) / previousValue) * 100) : null;

	if (hideWhenEmpty && !hasData) {
		return null;
	}

	return (
		<div className="dashboard-sales-card">
			<UnlockCelebration className="dashboard-unlock-celebration--sales" active={celebrateUnlock} />
			<div className="dashboard-sales-card__header">
				<div className="dashboard-sales-card__title-wrap">
					<span className="dashboard-sales-card__eyebrow">{title}</span>
					<h3 className="dashboard-sales-card__headline">
						<ProtectedValue protectedView={protectedView} visibleValue={formatExactCurrency(currentValue)} className="dashboard-protected-value--headline" maskedClassName="dashboard-protected-value__content--headline-masked" />
					</h3>
					{currentLabel ? <p className="dashboard-sales-card__subtitle">{currentLabel}</p> : null}
				</div>
				{dashboardProtectionEnabled && !protectedView ? (
					<Button className="dashboard-sales-card__toggle" type={protectedView ? 'primary' : 'default'} icon={protectedView ? <EyeOutlined /> : <EyeInvisibleOutlined />} onClick={onAccessToggle}>
						{protectedView ? t('dashboard.show_amounts') : t('dashboard.hide_amounts')}
					</Button>
				) : null}
				<div className="dashboard-sales-card__stats">
					<div className="dashboard-sales-card__stat">
						<span>{t('dashboard.previous_month')}</span>
						<strong>
							<ProtectedValue protectedView={protectedView} visibleValue={formatExactCurrency(previousValue)} className="dashboard-protected-value--stat" maskedClassName="dashboard-protected-value__content--stat-masked" />
						</strong>
					</div>
					<div className="dashboard-sales-card__stat dashboard-sales-card__stat--accent">
						<span>{t('dashboard.this_month')}</span>
						<strong>
							<ProtectedValue protectedView={protectedView} visibleValue={formatExactCurrency(currentValue)} className="dashboard-protected-value--stat" maskedClassName="dashboard-protected-value__content--stat-masked" />
						</strong>
					</div>
					{trend !== null ? <span className={`dashboard-sales-card__trend ${trend >= 0 ? 'is-positive' : 'is-negative'}`}>{trend >= 0 ? `+${trend}%` : `${trend}%`}</span> : null}
				</div>
			</div>
			<div className="dashboard_earning_chart">
				{protectedView ? (
					<div className="dashboard-sales-card__locked-state">
						<span className="dashboard-sales-card__locked-label">{t('dashboard.protected_amounts')}</span>
						<p>{t('dashboard.unlock_sales_copy')}</p>
						{dashboardProtectionEnabled ? (
							<Button type="primary" icon={<EyeOutlined />} onClick={onAccessToggle}>
								{t('dashboard.show_amounts')}
							</Button>
						) : null}
					</div>
				) : (
					<LineChartGradient {...data} label={title} />
				)}
			</div>
		</div>
	);
};

export default DashboardPresentational;
